import React, { useCallback, useRef, useState } from "react";
import classNames from "classnames";
import { useWindowDimensions } from "../../../utils/hooks/windowDimensions";
import styles from "./Sticker.module.scss";
import { clamp } from "../../../utils/utils";

export interface StickerProps {
  startingLeft: number;
  startingTop: number;
  className?: string;
  onStart?: () => void;
  onEnd?: () => void;
  disabled?: boolean;
}

type Position = {
  left: number;
  top: number;
};

type ClientPosition = {
  clientX: number;
  clientY: number;
};

const getClientPosition = (e: TouchEvent | MouseEvent): ClientPosition => {
  if ("touches" in e) {
    return {
      clientX: e.touches[0].clientX,
      clientY: e.touches[0].clientY,
    };
  }

  return {
    clientX: e.clientX,
    clientY: e.clientY,
  };
};

const Sticker: React.FC<StickerProps> = ({
  startingLeft,
  startingTop,
  className,
  children,
  onStart,
  onEnd,
  disabled,
}) => {
  const { width, height } = useWindowDimensions();
  const [position, setPosition] = useState<Position>({
    left: startingLeft,
    top: startingTop,
  });
  const stickerRef = useRef<HTMLDivElement>(null);

  const onDragStart = useCallback(
    (position: ClientPosition) => {
      const target = stickerRef.current as HTMLDivElement;

      const offsetX = position.clientX - target.offsetLeft;
      const offsetY = position.clientY - target.offsetTop + window.pageYOffset;
      const startX = position.clientX;
      const startY = position.clientY;

      const { width: targetWidth } = target.getBoundingClientRect();

      const maxLeft = 1 - targetWidth / width;

      let started = false;

      const onDragMove = (e: MouseEvent | TouchEvent) => {
        e.preventDefault();
        const { clientX, clientY } = getClientPosition(e);

        const clickY = clientY + window.pageYOffset - offsetY;
        const clickX = clientX + window.pageXOffset - offsetX;

        if (
          (Math.abs(startX - clientX) > 10 ||
            Math.abs(startY - clientY) > 10) &&
          !started
        ) {
          document.body.classList.add("sticker-lock");

          started = true;

          if (onStart) {
            onStart();
          }
        }

        setPosition({
          left: clamp(clickX / width, 0, maxLeft),
          top: clickY / height,
        });
      };

      const onDragEnd = () => {
        window.removeEventListener("mousemove", onDragMove);
        window.removeEventListener("touchmove", onDragMove);
        window.removeEventListener("mouseup", onDragEnd);
        window.removeEventListener("touchend", onDragEnd);
        document.body.classList.remove("sticker-lock");
        if (onEnd) {
          onEnd();
        }
      };

      window.addEventListener("mousemove", onDragMove, { passive: false });
      window.addEventListener("touchmove", onDragMove, { passive: false });
      window.addEventListener("mouseup", onDragEnd);
      window.addEventListener("touchend", onDragEnd);
    },
    [height, onEnd, onStart, width]
  );

  const onDragInitiate = useCallback(
    (
      e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
    ) => {
      if (disabled) {
        return;
      }

      e.preventDefault();

      onDragStart(getClientPosition(e.nativeEvent));
    },
    [disabled, onDragStart]
  );

  return (
    <div
      ref={stickerRef}
      className={classNames(styles.sticker, className)}
      onMouseDown={onDragInitiate}
      onTouchStart={onDragInitiate}
      style={{
        left: `${width * position.left}px`,
        top: `${height * position.top}px`,
      }}
    >
      {children}
    </div>
  );
};

export { Sticker };
